import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Button, Icon, Menu, Segment, Header, Table, Message, Loader, Divider, Modal } from 'semantic-ui-react';
import apiClient, { api } from '../api/apiClient';
import { formatStatus } from '../utils/requestHelpers';
import { formatTimestamp } from '../utils/timezone.utils';
import PaginationFooter from './PaginationFooter';
import FilterPanel from './FilterPanel';
import { useTablePreferences } from '../hooks/useTablePreferences';
import {
    canAccessDecommissionApprovalSubTab,
    canAccessDecommissionRequestSubTab,
    canAccessDecommissionSubApprovalSubTab,
    canCompleteDecommission,
    canExecuteDecommission,
    canSubApproveDecommission,
    formatDecommissionSubmitterStatus,
    getDecommissionSubmitterStatusColor,
    getDefaultDecommissionSubTab
} from '../utils/decommissionRoles';
import RejectionModal from './RejectionModal';
import CreationLogsModal from './CreationLogsModal';
import '../azure/components/form/azureForm.css';

const sectionHeaderStyle = {
    color: '#062D4D',
    marginBottom: '1rem',
    fontWeight: 700
};

const DECOMMISSION_MESSAGE_DISMISS_MS = 3000;

const displayDecommissionValue = (value) => (value && value !== '' ? value : 'N/A');

const formatDecommissionDetailLabel = (key) => String(key || '')
    .replace(/[_-]/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const HIDDEN_ACTIVE_CLOUD_DETAIL_KEYS = new Set([
    'keepercredentialscreated',
    'updatedat',
    'mongoupdated',
    'modsecupdated',
    'registrationkey',
    'requestid',
    'mailsent',
    'id',
    'dburl',
    'validityperiod'
]);

const ACTIVE_CLOUD_DETAIL_FIELD_ORDER = [
    { label: 'Cloud Name', keys: ['cloudName', 'cloud_name', 'cloud', 'name'] },
    { label: 'Status', keys: ['status'] },
    { label: 'Validity Period', keys: ['validityPeriodStr', 'validity_period_str'] },
    { label: 'Subscription Status', keys: ['subscriptionStatus', 'subscription_status'] },
    { label: 'Customer Name', keys: ['customerName', 'customer_name'] },
    { label: 'Provisioning Source', keys: ['provisioningSource', 'provisioning_source'] },
    { label: 'Customer Type', keys: ['customerType', 'customer_type'] },
    { label: 'Country Code', keys: ['countryCode', 'country_code'] },
    { label: 'Profile', keys: ['profile'] },
    { label: 'Ceph Storage', keys: ['cephStorage', 'ceph_storage'] },
    { label: 'Portal License Updated', keys: ['portalLicenseUpdated', 'portal_license_updated'] },
    { label: 'Portal Url', keys: ['portalUrl', 'portal_url'] },
    { label: 'Created At', keys: ['createdAt', 'created_at'] }
];

const normalizeDecommissionDetailKey = (key) => String(key || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

const shouldHideDecommissionDetailKey = (key) =>
    HIDDEN_ACTIVE_CLOUD_DETAIL_KEYS.has(normalizeDecommissionDetailKey(key));

const isTimestampDetailKey = (key) => {
    const normalized = normalizeDecommissionDetailKey(key);
    return normalized === 'time' || normalized.endsWith('time') || normalized.endsWith('date');
};

const formatDecommissionDetailDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString();
};

const isEpochDetailValue = (value) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) && (
        (numeric >= 1000000000 && numeric <= 9999999999) ||
        (numeric >= 1000000000000 && numeric <= 9999999999999)
    );
};

const formatDecommissionPrimitiveDetailValue = (value, key) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    if (isTimestampDetailKey(key) || isEpochDetailValue(value)) {
        const numericValue = Number(value);
        const dateValue =
            Number.isFinite(numericValue) && /^\d{10,13}$/.test(String(value))
                ? (numericValue < 10000000000 ? numericValue * 1000 : numericValue)
                : value;
        const formattedDate = formatDecommissionDetailDate(dateValue);
        if (formattedDate) return formattedDate;
    }

    return String(value);
};

const renderDecommissionDetailValue = (value, key = '') => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value !== 'object') return formatDecommissionPrimitiveDetailValue(value, key);

    if (Array.isArray(value)) {
        if (!value.length) return 'N/A';
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {value.map((item, index) => (
                    <div key={index}>{renderDecommissionDetailValue(item, key)}</div>
                ))}
            </div>
        );
    }

    const entries = Object.entries(value).filter(([entryKey, val]) =>
        !shouldHideDecommissionDetailKey(entryKey) &&
        val !== null &&
        val !== undefined &&
        val !== ''
    );
    if (!entries.length) return 'N/A';

    return (
        <Table compact="very" definition>
            <Table.Body>
                {entries.map(([key, val]) => (
                    <Table.Row key={key}>
                        <Table.Cell width={5}>{formatDecommissionDetailLabel(key)}</Table.Cell>
                        <Table.Cell>{renderDecommissionDetailValue(val, key)}</Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
};

const getActiveCloudDetailValue = (details, keys) => {
    if (!details || typeof details !== 'object' || Array.isArray(details)) return undefined;
    const entries = Object.entries(details);
    const normalizedKeys = keys.map(normalizeDecommissionDetailKey);
    const match = entries.find(([entryKey]) =>
        normalizedKeys.includes(normalizeDecommissionDetailKey(entryKey))
    );
    return match ? match[1] : undefined;
};

const renderActiveCloudDetailTable = (details) => {
    const rows = ACTIVE_CLOUD_DETAIL_FIELD_ORDER
        .map(({ label, keys }) => ({
            label,
            key: keys[0],
            value: getActiveCloudDetailValue(details, keys)
        }))
        .filter(({ value }) => value !== null && value !== undefined && value !== '');

    if (!rows.length) return <Message info>No requested active cloud details available.</Message>;

    return (
        <Table compact="very" definition>
            <Table.Body>
                {rows.map(({ label, key, value }) => (
                    <Table.Row key={label}>
                        <Table.Cell width={5}>{label}</Table.Cell>
                        <Table.Cell>{renderDecommissionDetailValue(value, key)}</Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
};

const renderActiveCloudDetails = (details) => {
    if (Array.isArray(details)) {
        if (!details.length) return <Message info>No requested active cloud details available.</Message>;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {details.map((item, index) => (
                    <div key={index}>{renderActiveCloudDetailTable(item)}</div>
                ))}
            </div>
        );
    }

    return renderActiveCloudDetailTable(details);
};

const getDecommissionStatusTextColor = (status) => {
    if (!status) return '#062D4D';

    const normalized = String(status).toUpperCase();

    if (normalized === 'REJECTED' || normalized === 'FAILED') return '#CE1B4E';
    if (normalized === 'APPROVED') return '#6F9911';
    if (
        normalized.includes('PENDING') ||
        normalized === 'IN_PROGRESS' ||
        normalized === 'DEPROVISIONED_MANUALLY'
    ) {
        return '#35B7F4';
    }

    return '#062D4D';
};

const formatDecommissionActualStatus = (status) => {
    if (!status) return 'Unknown';

    switch (String(status).toUpperCase()) {
        case 'IN_PROGRESS':
            return 'Decommissioning';
        case 'DEPROVISIONED_MANUALLY':
            return 'Storage Decommission Is In Progress';
        case 'APPROVED':
            return 'Decommission Successful';
        case 'FAILED':
            return 'Decommission Failed';
        case 'PENDING_SUB_APPROVER':
        case 'PENDING_APPROVER':
            return 'Pending Sub-Approver';
        case 'PENDING_DEVOPS_APPROVAL':
            return 'Pending DevOps Approval';
        case 'REJECTED':
            return 'Rejected';
        default:
            return formatStatus(status);
    }
};

const getDecommissionActualStatusColor = (status) => {
    const normalized = String(status || '').toUpperCase();

    if (normalized === 'APPROVED') return 'positive';
    if (normalized === 'FAILED' || normalized === 'REJECTED') return 'negative';
    if (
        normalized === 'IN_PROGRESS' ||
        normalized === 'DEPROVISIONED_MANUALLY' ||
        normalized === 'PENDING_SUB_APPROVER' ||
        normalized === 'PENDING_APPROVER' ||
        normalized === 'PENDING_DEVOPS_APPROVAL'
    ) {
        return 'warning';
    }
    return '';
};

const DecommissionRequestDetailsModal = ({
    open,
    onClose,
    request,
    formatStatusFn = formatStatus
}) => {
    const [copyRequestIdSuccess, setCopyRequestIdSuccess] = useState(false);
    const [copyCloudNameSuccess, setCopyCloudNameSuccess] = useState(false);

    const copyToClipboard = async (text, setSuccess) => {
        const value = (text ?? '').toString();
        if (!value) return;

        try {
            await navigator.clipboard.writeText(value);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = value;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setSuccess(true);
                setTimeout(() => setSuccess(false), 2000);
            } catch {
                // ignore
            }
            document.body.removeChild(textArea);
        }
    };

    if (!request) {
        return null;
    }

    const requestId = request.requestId || request.id;
    const cloudName = (request.cloudName || '').toString().trim();
    const isRejected = request.status === 'REJECTED';
    const wasSubStageRejection = isRejected && !request.subApprovedBy;
    const wasDevopsStageRejection = isRejected && !!request.subApprovedBy;
    const statusLabel = formatStatusFn(request.status);
    const statusColor = getDecommissionStatusTextColor(request.status);
    const decommissionReason = request.decommissionReason || request.decommission_reason || '';

    const subApprovedBy =
        request.subApprovedBy || (wasSubStageRejection ? request.rejectedBy : '');
    const subApprovedAt =
        request.subApprovedAt ||
        (wasSubStageRejection ? request.rejectedAt : null);
    const subApproverComment =
        request.subApproverComment ||
        (wasSubStageRejection ? request.rejectionReason : '');

    const approvedBy =
        request.approvedBy || (wasDevopsStageRejection ? request.rejectedBy : '');
    const approverAt =
        request.approvedAt ||
        (request.status === 'FAILED' ? request.updatedAt : null) ||
        (wasDevopsStageRejection ? request.rejectedAt : null);
    const approverComment =
        request.approverComment ||
        (wasDevopsStageRejection ? request.rejectionReason : '');

    const showSubApproverComment =
        !!subApproverComment && (wasSubStageRejection || !!request.subApproverComment);

    return (
        <Modal open={open} onClose={onClose} size="large">
            <Modal.Header>Request Details</Modal.Header>
            <Modal.Content>
                <Table definition>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>General Request Information</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell width={4}>Request ID</Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{displayDecommissionValue(requestId)}</span>
                                    {requestId && (
                                        <Icon
                                            name={copyRequestIdSuccess ? 'check' : 'copy'}
                                            link
                                            onClick={() =>
                                                copyToClipboard(requestId, setCopyRequestIdSuccess)
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                color: copyRequestIdSuccess ? '#6F9911' : '#35B7F4',
                                                margin: 0,
                                                padding: 0,
                                                display: 'inline-flex',
                                                alignItems: 'center'
                                            }}
                                            title={copyRequestIdSuccess ? 'Copied!' : 'Copy Request ID'}
                                        />
                                    )}
                                </div>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Submitted By</Table.Cell>
                            <Table.Cell>{displayDecommissionValue(request.submittedBy)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Submission Time</Table.Cell>
                            <Table.Cell>{formatTimestamp(request.timestamp)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Current Status</Table.Cell>
                            <Table.Cell style={{ color: statusColor, fontWeight: 600 }}>
                                {statusLabel}
                            </Table.Cell>
                        </Table.Row>

                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>Decommission Details</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Cloud Name</Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{displayDecommissionValue(cloudName)}</span>
                                    {cloudName && (
                                        <Icon
                                            name={copyCloudNameSuccess ? 'check' : 'copy'}
                                            link
                                            onClick={() =>
                                                copyToClipboard(cloudName, setCopyCloudNameSuccess)
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                color: copyCloudNameSuccess ? '#6F9911' : '#35B7F4',
                                                margin: 0,
                                                padding: 0,
                                                display: 'inline-flex',
                                                alignItems: 'center'
                                            }}
                                            title={
                                                copyCloudNameSuccess ? 'Copied!' : 'Copy Cloud Name'
                                            }
                                        />
                                    )}
                                </div>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Reason for Decommission</Table.Cell>
                            <Table.Cell>{displayDecommissionValue(decommissionReason)}</Table.Cell>
                        </Table.Row>

                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>Approval Details</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50" style={{ fontSize: '0.95em' }}>
                                <strong>Sub Approver</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>
                                {wasSubStageRejection ? 'Sub Approver' : 'Sub Approved By'}
                            </Table.Cell>
                            <Table.Cell>{displayDecommissionValue(subApprovedBy)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>
                                {wasSubStageRejection ? 'Sub Approver At' : 'Sub Approved At'}
                            </Table.Cell>
                            <Table.Cell>{formatTimestamp(subApprovedAt)}</Table.Cell>
                        </Table.Row>
                        {showSubApproverComment && (
                            <Table.Row>
                                <Table.Cell>Sub Approver Comment</Table.Cell>
                                <Table.Cell>{displayDecommissionValue(subApproverComment)}</Table.Cell>
                            </Table.Row>
                        )}

                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50" style={{ fontSize: '0.95em' }}>
                                <strong>Approver (DevOps)</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>
                                {wasDevopsStageRejection ? 'Rejected By' : 'Approved By'}
                            </Table.Cell>
                            <Table.Cell>{displayDecommissionValue(approvedBy)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Approver Comment</Table.Cell>
                            <Table.Cell>{displayDecommissionValue(approverComment)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Approver At</Table.Cell>
                            <Table.Cell>{formatTimestamp(approverAt)}</Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Close</Button>
            </Modal.Actions>
        </Modal>
    );
};

const DecommissionHistoryTable = ({
    requests,
    showSubmittedBy = true,
    showSubApprovedBy = false,
    formatStatusFn = formatDecommissionActualStatus,
    getStatusColorFn = getDecommissionActualStatusColor,
    showActions = false,
    onViewDetails
}) => (
    <div style={{ overflowX: 'auto' }}>
        <Table celled striped>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Timestamp</Table.HeaderCell>
                    <Table.HeaderCell>Request ID</Table.HeaderCell>
                    <Table.HeaderCell>Cloud Name</Table.HeaderCell>
                    {showSubmittedBy && <Table.HeaderCell>Submitted By</Table.HeaderCell>}
                    {showSubApprovedBy && <Table.HeaderCell>Sub Approved By</Table.HeaderCell>}
                    <Table.HeaderCell>Current Status</Table.HeaderCell>
                    <Table.HeaderCell>Approved By</Table.HeaderCell>
                    <Table.HeaderCell>Rejected By</Table.HeaderCell>
                    {showActions && <Table.HeaderCell>Actions</Table.HeaderCell>}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {requests.map((request) => {
                    const isRejected = request.status === 'REJECTED';

                    return (
                        <Table.Row key={request.requestId || request.id}>
                            <Table.Cell>{formatTimestamp(request.timestamp)}</Table.Cell>
                            <Table.Cell>{request.requestId || request.id || '—'}</Table.Cell>
                            <Table.Cell>{request.cloudName || '—'}</Table.Cell>
                            {showSubmittedBy && <Table.Cell>{request.submittedBy || '—'}</Table.Cell>}
                            {showSubApprovedBy && (
                                <Table.Cell>{request.subApprovedBy || '—'}</Table.Cell>
                            )}
                            <Table.Cell className={getStatusColorFn(request.status)}>
                                {formatStatusFn(request.status)}
                            </Table.Cell>
                            <Table.Cell>{isRejected ? '—' : (request.approvedBy || '—')}</Table.Cell>
                            <Table.Cell>{isRejected ? (request.rejectedBy || '—') : '—'}</Table.Cell>
                            {showActions && (
                                <Table.Cell>
                                    <Button
                                        size="small"
                                        primary
                                        onClick={() => onViewDetails?.(request)}
                                    >
                                        View Details
                                    </Button>
                                </Table.Cell>
                            )}
                        </Table.Row>
                    );
                })}
            </Table.Body>
        </Table>
    </div>
);

const DecommissionSubmittedRequestsTable = ({
    requests,
    loading,
    error,
    title,
    headerActions = null,
    emptyTitle,
    emptyMessage,
    showSubmittedBy = false,
    showSubApprovedBy = false,
    useSubmitterStatusLabels = false,
    showActions = true,
    allowFilterToggle = true
}) => {
    const [detailsRequest, setDetailsRequest] = useState(null);
    const [requestIdFilter, setRequestIdFilter] = useState('');
    const [cloudNameFilter, setCloudNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [globalSearch, setGlobalSearch] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [dateRangeStart, setDateRangeStart] = useState('');
    const [dateRangeEnd, setDateRangeEnd] = useState('');
    const [submittedByFilter, setSubmittedByFilter] = useState('');
    const [filterPresetName, setFilterPresetName] = useState('');
    const [savedFilters, setSavedFilters] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('decommission-history-filters') || '[]');
        } catch {
            return [];
        }
    });
    const [showFilters, setShowFilters] = useState(false);
    const { rowsPerPage, activePage, setActivePage } = useTablePreferences();
    const formatStatusFn = useSubmitterStatusLabels
        ? formatDecommissionSubmitterStatus
        : formatDecommissionActualStatus;
    const getStatusColorFn = useSubmitterStatusLabels
        ? getDecommissionSubmitterStatusColor
        : getDecommissionActualStatusColor;
    const availableStatuses = useMemo(() => {
        return Array.from(
            new Set((requests || []).map((r) => String(r?.status || '').trim()).filter(Boolean))
        );
    }, [requests]);

    const activeFilterCount = [
        requestIdFilter,
        cloudNameFilter,
        categoryFilter,
        statusFilter,
        globalSearch,
        selectedStatuses.length,
        selectedCategories.length,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter
    ].filter((v) => v && v !== 0).length;

    const hasActiveFilters = activeFilterCount > 0;

    const filteredRequests = useMemo(() => {
        const requestIdTerm = requestIdFilter.trim().toLowerCase();
        const cloudNameTerm = cloudNameFilter.trim().toLowerCase();
        const categoryTerm = categoryFilter.trim().toLowerCase();
        const globalTerm = globalSearch.trim().toLowerCase();
        const submittedByTerm = submittedByFilter.trim().toLowerCase();
        const statusTerm = statusFilter.trim().toUpperCase();
        const selectedStatusTerms = selectedStatuses.map((status) => String(status || '').toUpperCase());
        const selectedCategoryTerms = selectedCategories.map((category) => String(category || '').toLowerCase());
        const startTime = dateRangeStart ? new Date(`${dateRangeStart}T00:00:00`).getTime() : null;
        const endTime = dateRangeEnd ? new Date(`${dateRangeEnd}T23:59:59.999`).getTime() : null;

        return (requests || []).filter((request) => {
            const requestIdValue = String(request?.requestId || request?.id || '').toLowerCase();
            const cloudNameValue = String(request?.cloudName || '').toLowerCase();
            const reasonValue = String(request?.decommissionReason || '').toLowerCase();
            const submittedByValue = String(request?.submittedBy || '').toLowerCase();
            const subApprovedByValue = String(request?.subApprovedBy || '').toLowerCase();
            const approvedByValue = String(request?.approvedBy || '').toLowerCase();
            const rejectedByValue = String(request?.rejectedBy || '').toLowerCase();
            const statusValue = String(request?.status || '').toUpperCase();
            const formattedStatusValue = formatStatusFn(request?.status).toLowerCase();
            const requestTime = new Date(request?.timestamp).getTime();
            const globalValue = [
                requestIdValue,
                cloudNameValue,
                reasonValue,
                submittedByValue,
                subApprovedByValue,
                approvedByValue,
                rejectedByValue,
                statusValue.toLowerCase(),
                formattedStatusValue,
                String(request?.timestamp || '').toLowerCase()
            ].join(' ');

            if (requestIdTerm && !requestIdValue.includes(requestIdTerm)) return false;
            if (cloudNameTerm && !cloudNameValue.includes(cloudNameTerm)) return false;
            if (categoryTerm && !reasonValue.includes(categoryTerm)) return false;
            if (globalTerm && !globalValue.includes(globalTerm)) return false;
            if (submittedByTerm && !submittedByValue.includes(submittedByTerm)) return false;
            if (
                statusTerm &&
                !statusValue.includes(statusTerm) &&
                !formattedStatusValue.includes(statusTerm.toLowerCase())
            ) {
                return false;
            }
            if (selectedStatusTerms.length && !selectedStatusTerms.includes(statusValue)) return false;
            if (selectedCategoryTerms.length && !selectedCategoryTerms.some((term) => reasonValue.includes(term))) return false;
            if (startTime && (!Number.isFinite(requestTime) || requestTime < startTime)) return false;
            if (endTime && (!Number.isFinite(requestTime) || requestTime > endTime)) return false;
            return true;
        });
    }, [
        requests,
        requestIdFilter,
        cloudNameFilter,
        categoryFilter,
        globalSearch,
        submittedByFilter,
        statusFilter,
        selectedStatuses,
        selectedCategories,
        dateRangeStart,
        dateRangeEnd,
        formatStatusFn
    ]);

    const totalPages = Math.max(1, Math.ceil((filteredRequests.length || 0) / rowsPerPage));
    const safePage = Math.min(activePage, totalPages);
    const pageStart = (safePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const pagedRequests = filteredRequests.slice(pageStart, pageEnd);

    useEffect(() => {
        if (activePage !== safePage) {
            setActivePage(safePage);
        }
    }, [activePage, safePage, setActivePage]);

    const handleFilterChange = () => {
        setActivePage(1);
    };

    const resetFilters = () => {
        setRequestIdFilter('');
        setCloudNameFilter('');
        setCategoryFilter('');
        setStatusFilter('');
        setGlobalSearch('');
        setSelectedStatuses([]);
        setSelectedCategories([]);
        setDateRangeStart('');
        setDateRangeEnd('');
        setSubmittedByFilter('');
        setActivePage(1);
    };

    const saveCurrentFilters = () => {
        const name = filterPresetName.trim();
        if (!name) return;

        const nextPreset = {
            id: `${Date.now()}`,
            name,
            createdAt: new Date().toISOString(),
            filters: {
                requestIdFilter,
                cloudNameFilter,
                categoryFilter,
                statusFilter,
                globalSearch,
                selectedStatuses,
                selectedCategories,
                dateRangeStart,
                dateRangeEnd,
                submittedByFilter
            }
        };
        const nextSavedFilters = [...savedFilters, nextPreset];
        setSavedFilters(nextSavedFilters);
        localStorage.setItem('decommission-history-filters', JSON.stringify(nextSavedFilters));
        setFilterPresetName('');
    };

    const loadFilterPreset = (preset) => {
        const filters = preset?.filters || {};
        setRequestIdFilter(filters.requestIdFilter || '');
        setCloudNameFilter(filters.cloudNameFilter || '');
        setCategoryFilter(filters.categoryFilter || '');
        setStatusFilter(filters.statusFilter || '');
        setGlobalSearch(filters.globalSearch || '');
        setSelectedStatuses(filters.selectedStatuses || []);
        setSelectedCategories(filters.selectedCategories || []);
        setDateRangeStart(filters.dateRangeStart || '');
        setDateRangeEnd(filters.dateRangeEnd || '');
        setSubmittedByFilter(filters.submittedByFilter || '');
        setActivePage(1);
    };

    const deleteFilterPreset = (presetId) => {
        const nextSavedFilters = savedFilters.filter((preset) => preset.id !== presetId);
        setSavedFilters(nextSavedFilters);
        localStorage.setItem('decommission-history-filters', JSON.stringify(nextSavedFilters));
    };

    const renderHeaderControls = () => {
        if (!title && !headerActions && !allowFilterToggle) return null;

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    gap: '1rem'
                }}
            >
                {title ? (
                    <Header
                        as="h3"
                        style={{
                            ...sectionHeaderStyle,
                            margin: 0
                        }}
                    >
                        {title}
                    </Header>
                ) : (
                    <div />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {headerActions}
                    {allowFilterToggle && (
                        <Button
                            icon
                            labelPosition="left"
                            onClick={() => setShowFilters(!showFilters)}
                            color={showFilters ? 'blue' : undefined}
                            primary={hasActiveFilters && !showFilters}
                        >
                            <Icon name="filter" />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                            {hasActiveFilters && !showFilters && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: '8px',
                                    backgroundColor: '#fff',
                                    color: '#2185d0',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '12px',
                                    padding: '0 7px',
                                    lineHeight: 1,
                                    letterSpacing: '0.3px'
                                }}>
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <>
                {renderHeaderControls()}
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <Loader active inline="centered">Loading requests...</Loader>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                {renderHeaderControls()}
                <Message negative>{error}</Message>
            </>
        );
    }

    if (!requests.length) {
        return (
            <>
                {renderHeaderControls()}
                <Message info>
                    <Message.Header>{emptyTitle || 'No submitted requests'}</Message.Header>
                    <p>{emptyMessage || 'You have not submitted any decommission requests yet.'}</p>
                </Message>
            </>
        );
    }

    return (
        <>
            {renderHeaderControls()}

            {(!allowFilterToggle || showFilters) && (
                <FilterPanel
                    mode="advanced"
                    onClose={allowFilterToggle ? () => setShowFilters(false) : undefined}
                    statusFilter={statusFilter}
                    cloudNameFilter={cloudNameFilter}
                    categoryFilter={categoryFilter}
                    requestIdFilter={requestIdFilter}
                    globalSearch={globalSearch}
                    selectedStatuses={selectedStatuses}
                    selectedCategories={selectedCategories}
                    dateRangeStart={dateRangeStart}
                    dateRangeEnd={dateRangeEnd}
                    submittedByFilter={submittedByFilter}
                    savedFilters={savedFilters}
                    filterPresetName={filterPresetName}
                    availableStatuses={availableStatuses}
                    availableCategories={[]}
                    onStatusFilterChange={setStatusFilter}
                    onCloudNameFilterChange={setCloudNameFilter}
                    onCategoryFilterChange={setCategoryFilter}
                    onRequestIdFilterChange={setRequestIdFilter}
                    onGlobalSearchChange={setGlobalSearch}
                    onSelectedStatusesChange={setSelectedStatuses}
                    onSelectedCategoriesChange={setSelectedCategories}
                    onDateRangeStartChange={setDateRangeStart}
                    onDateRangeEndChange={setDateRangeEnd}
                    onSubmittedByFilterChange={setSubmittedByFilter}
                    onFilterPresetNameChange={setFilterPresetName}
                    onSaveFilterPreset={saveCurrentFilters}
                    onLoadFilterPreset={loadFilterPreset}
                    onDeleteFilterPreset={deleteFilterPreset}
                    onResetFilters={resetFilters}
                    hasActiveFilters={hasActiveFilters}
                    onFilterChange={handleFilterChange}
                />
            )}

            {!filteredRequests.length ? (
                <Message info>
                    <Message.Header>No matching requests</Message.Header>
                    <p>Try adjusting or clearing the filters.</p>
                </Message>
            ) : (
                <>
            <DecommissionHistoryTable
                requests={pagedRequests}
                showSubmittedBy={showSubmittedBy}
                showSubApprovedBy={showSubApprovedBy}
                formatStatusFn={formatStatusFn}
                getStatusColorFn={getStatusColorFn}
                showActions={showActions}
                onViewDetails={setDetailsRequest}
            />
            <PaginationFooter
                activePage={safePage}
                totalPages={totalPages}
                totalItems={requests.length}
                filteredItems={filteredRequests}
                hasActiveFilters={filteredRequests.length !== requests.length}
                onPageChange={setActivePage}
                pageStart={pageStart}
                pageEnd={pageEnd}
            />
                </>
            )}
            <DecommissionRequestDetailsModal
                open={!!detailsRequest}
                request={detailsRequest}
                onClose={() => setDetailsRequest(null)}
                formatStatusFn={formatStatusFn}
            />
        </>
    );
};

const DecommissionRequestPanel = ({ userEmail, requests, loading, error, onRefresh }) => {
    const [cloudName, setCloudName] = useState('');
    const [cloudOptions, setCloudOptions] = useState([]);
    const [cloudOptionsLoading, setCloudOptionsLoading] = useState(false);
    const [cloudOptionsError, setCloudOptionsError] = useState(null);
    const [activeCloudDetails, setActiveCloudDetails] = useState(null);
    const [activeCloudDetailsLoading, setActiveCloudDetailsLoading] = useState(false);
    const [activeCloudDetailsError, setActiveCloudDetailsError] = useState(null);
    const [activeCloudDetailsCache, setActiveCloudDetailsCache] = useState({});
    const selectedCloudNameRef = useRef('');
    const [decommissionReason, setDecommissionReason] = useState('');
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitMessage, setSubmitMessage] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingCloudName, setPendingCloudName] = useState('');
    const [pendingDecommissionReason, setPendingDecommissionReason] = useState('');

    const fetchActiveClouds = useCallback(async () => {
        setCloudOptionsLoading(true);
        setCloudOptionsError(null);
        try {
            const response = await api.multiTenancyDecommission.getActiveClouds();
            const items = Array.isArray(response?.data?.options)
                ? response.data.options
                : Array.isArray(response?.data?.data)
                    ? response.data.data
                    : [];
            const options = items
                .map((item) => {
                    const value = typeof item === 'string'
                        ? item
                        : item?.value || item?.cloudName || item?.cloud_name || item?.name || item?.key;
                    const trimmedValue = String(value || '').trim();
                    if (!trimmedValue) return null;
                    return {
                        key: trimmedValue,
                        value: trimmedValue,
                        text: item?.text || item?.label || trimmedValue
                    };
                })
                .filter(Boolean);
            setCloudOptions(options);
        } catch (err) {
            setCloudOptions([]);
            setCloudOptionsError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load active clouds'
            );
        } finally {
            setCloudOptionsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchActiveClouds();
    }, [fetchActiveClouds]);

    useEffect(() => {
        if (!submitMessage && !submitError) return undefined;

        const timer = window.setTimeout(() => {
            setSubmitMessage(null);
            setSubmitError(null);
        }, DECOMMISSION_MESSAGE_DISMISS_MS);

        return () => window.clearTimeout(timer);
    }, [submitMessage, submitError]);

    const fetchActiveCloudDetails = useCallback(async (selectedCloudName) => {
        const trimmedCloudName = (selectedCloudName || '').toString().trim();
        if (!trimmedCloudName) return;
        const cacheKey = trimmedCloudName.toLowerCase();

        if (activeCloudDetailsCache[cacheKey]) {
            setActiveCloudDetails(activeCloudDetailsCache[cacheKey]);
            setActiveCloudDetailsLoading(false);
            setActiveCloudDetailsError(null);
            return;
        }

        setActiveCloudDetailsLoading(true);
        setActiveCloudDetailsError(null);
        setActiveCloudDetails(null);
        try {
            const response = await api.multiTenancyDecommission.getActiveCloudDetails(trimmedCloudName);
            const details = response?.data?.data ?? response?.data ?? null;
            setActiveCloudDetailsCache((prev) => ({
                ...prev,
                [cacheKey]: details
            }));
            if (selectedCloudNameRef.current === trimmedCloudName) {
                setActiveCloudDetails(details);
            }
        } catch (err) {
            if (selectedCloudNameRef.current === trimmedCloudName) {
                setActiveCloudDetailsError(
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to load active cloud details'
                );
            }
        } finally {
            if (selectedCloudNameRef.current === trimmedCloudName) {
                setActiveCloudDetailsLoading(false);
            }
        }
    }, [activeCloudDetailsCache]);

    const handleClear = () => {
        selectedCloudNameRef.current = '';
        setCloudName('');
        setDecommissionReason('');
        setErrors({});
        setSubmitError(null);
        setSubmitMessage(null);
        setShowConfirmModal(false);
        setPendingCloudName('');
        setPendingDecommissionReason('');
        setActiveCloudDetails(null);
        setActiveCloudDetailsError(null);
        setActiveCloudDetailsLoading(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmedCloudName = (cloudName || '').toString().trim();
        const trimmedReason = (decommissionReason || '').toString().trim();
        const nextErrors = {};

        if (!trimmedCloudName) {
            nextErrors.cloudName = 'Cloud name is required';
        }
        if (!trimmedReason) {
            nextErrors.decommissionReason = 'Reason for decommission is required';
        }
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            return;
        }

        setErrors({});
        setSubmitError(null);
        setSubmitMessage(null);
        setPendingCloudName(trimmedCloudName);
        setPendingDecommissionReason(trimmedReason);
        selectedCloudNameRef.current = trimmedCloudName;
        setShowConfirmModal(true);
        fetchActiveCloudDetails(trimmedCloudName);
    };

    const handleConfirmSubmit = async () => {
        if (!pendingCloudName || !pendingDecommissionReason) return;

        setSubmitting(true);
        setSubmitError(null);
        setSubmitMessage(null);
        try {
            await api.multiTenancyDecommission.submitRequest({
                cloudName: pendingCloudName,
                decommissionReason: pendingDecommissionReason,
                submittedBy: userEmail
            });
            setCloudName('');
            setDecommissionReason('');
            setPendingCloudName('');
            setPendingDecommissionReason('');
            setActiveCloudDetails(null);
            setActiveCloudDetailsError(null);
            setShowConfirmModal(false);
            setSubmitMessage(`Submitted decommission request for "${pendingCloudName}".`);
            onRefresh();
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to submit decommission request';
            setSubmitError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {submitMessage && (
                <Message positive onDismiss={() => setSubmitMessage(null)}>
                    {submitMessage}
                </Message>
            )}

            {submitError && (
                <Message negative onDismiss={() => setSubmitError(null)}>
                    <Message.Header>Submission failed</Message.Header>
                    <p>{submitError}</p>
                </Message>
            )}

            <Form onSubmit={handleSubmit}>
                <Form.Field required>
                    <label>Cloud Name to Decommission</label>
                    <Form.Select
                        search
                        selection
                        clearable
                        name="cloudName"
                        placeholder={cloudOptionsLoading ? 'Loading active clouds...' : 'Select cloud name'}
                        options={cloudOptions}
                        value={cloudName}
                        onChange={(e, { value }) => {
                            const selectedCloudName = (value || '').toString();
                            selectedCloudNameRef.current = selectedCloudName;
                            setCloudName(selectedCloudName);
                            setActiveCloudDetails(null);
                            setActiveCloudDetailsError(null);
                            if (selectedCloudName) {
                                fetchActiveCloudDetails(selectedCloudName);
                            } else {
                                setActiveCloudDetailsLoading(false);
                            }
                            if (errors.cloudName) {
                                setErrors((prev) => ({ ...prev, cloudName: '' }));
                            }
                        }}
                        loading={cloudOptionsLoading}
                        disabled={cloudOptionsLoading || submitting || !cloudOptions.length}
                        noResultsMessage="No active clouds found"
                        error={errors.cloudName ? { content: errors.cloudName } : null}
                    />
                </Form.Field>
                {cloudOptionsError && (
                    <Message warning>
                        <Message.Header>Unable to load active clouds</Message.Header>
                        <p>{cloudOptionsError}</p>
                        <Button
                            type="button"
                            size="small"
                            onClick={fetchActiveClouds}
                            loading={cloudOptionsLoading}
                            disabled={cloudOptionsLoading}
                        >
                            Retry
                        </Button>
                    </Message>
                )}

                <Form.Field>
                    <label>
                        Reason for Decommission <span style={{ color: '#db2828' }}>*</span>
                    </label>
                    <Form.TextArea
                        name="decommissionReason"
                        placeholder="Enter reason for decommission"
                        value={decommissionReason}
                        onChange={(e, { value }) => {
                            setDecommissionReason(value);
                            if (errors.decommissionReason) {
                                setErrors((prev) => ({ ...prev, decommissionReason: '' }));
                            }
                        }}
                        error={
                            errors.decommissionReason
                                ? { content: errors.decommissionReason }
                                : null
                        }
                    />
                </Form.Field>

                <div className="button-container">
                    <Button type="button" onClick={handleClear} disabled={submitting}>
                        Clear
                    </Button>
                    <Button
                        type="submit"
                        primary
                        loading={submitting}
                        disabled={
                            submitting ||
                            !(cloudName || '').toString().trim() ||
                            !(decommissionReason || '').toString().trim()
                        }
                    >
                        Submit Request
                    </Button>
                </div>
            </Form>

            <Modal
                open={showConfirmModal}
                onClose={() => {
                    if (!submitting) {
                        setShowConfirmModal(false);
                        setPendingCloudName('');
                        setPendingDecommissionReason('');
                        setActiveCloudDetails(null);
                        setActiveCloudDetailsError(null);
                        setActiveCloudDetailsLoading(false);
                    }
                }}
                size="small"
                closeOnDimmerClick={!submitting}
            >
                <Modal.Header>Confirm Decommission Request</Modal.Header>
                <Modal.Content>
                    <Message warning>
                        <Message.Header>Please confirm before submitting</Message.Header>
                        <p style={{ marginBottom: 0 }}>
                            You are about to submit a multi-tenancy decommission request.
                        </p>
                    </Message>
                    <div style={{ marginTop: '1rem' }}>
                        <p style={{ margin: 0 }}>
                            <strong>Cloud Name to Decommission:</strong>{' '}
                            <span style={{ color: '#062D4D' }}>{pendingCloudName}</span>
                        </p>
                        <p style={{ margin: '0.5rem 0 0' }}>
                            <strong>Reason for Decommission:</strong>{' '}
                            <span style={{ color: '#062D4D' }}>{pendingDecommissionReason}</span>
                        </p>
                    </div>
                    <Divider />
                    <Header as="h4" style={{ color: '#062D4D' }}>
                        Cloud Details
                    </Header>
                    {activeCloudDetailsLoading ? (
                        <Loader active inline="centered">Loading active cloud details...</Loader>
                    ) : activeCloudDetailsError ? (
                        <Message negative>
                            <Message.Header>Unable to load active cloud details</Message.Header>
                            <p>{activeCloudDetailsError}</p>
                            <Button
                                type="button"
                                size="small"
                                onClick={() => fetchActiveCloudDetails(pendingCloudName)}
                            >
                                Retry
                            </Button>
                        </Message>
                    ) : activeCloudDetails ? (
                        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                            {renderActiveCloudDetails(activeCloudDetails)}
                        </div>
                    ) : (
                        <Message info>No active cloud details available.</Message>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={() => {
                            if (!submitting) {
                                setShowConfirmModal(false);
                                setPendingCloudName('');
                                setPendingDecommissionReason('');
                                setActiveCloudDetails(null);
                                setActiveCloudDetailsError(null);
                                setActiveCloudDetailsLoading(false);
                            }
                        }}
                        disabled={submitting}
                    >
                        Back to Form
                    </Button>
                    <Button
                        primary
                        onClick={handleConfirmSubmit}
                        loading={submitting}
                        disabled={submitting || activeCloudDetailsLoading}
                    >
                        Confirm & Submit
                    </Button>
                </Modal.Actions>
            </Modal>

            <Divider section style={{ marginTop: '2rem', marginBottom: '1.25rem' }} />

            <DecommissionSubmittedRequestsTable
                title="My Submitted Requests"
                requests={requests}
                loading={loading}
                error={error}
                useSubmitterStatusLabels
            />
        </>
    );
};

const DecommissionApproval = ({
    userEmail,
    userRoles = [],
    onActionComplete,
    approvalMode = 'devops',
    requiredAccessLabel = 'DevOps'
}) => {
    const isSubApprovalMode = approvalMode === 'sub';
    const [pendingRequests, setPendingRequests] = useState([]);
    const [historyRequests, setHistoryRequests] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [pendingError, setPendingError] = useState(null);
    const [historyError, setHistoryError] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [approveConfirmRequest, setApproveConfirmRequest] = useState(null);
    const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
    const [approveCloudDetails, setApproveCloudDetails] = useState(null);
    const [approveCloudDetailsLoading, setApproveCloudDetailsLoading] = useState(false);
    const [approveCloudDetailsError, setApproveCloudDetailsError] = useState(null);
    const [approveCloudDetailsCache, setApproveCloudDetailsCache] = useState({});
    const approveCloudNameRef = useRef('');
    const [processingApproveId, setProcessingApproveId] = useState(null);
    const [processingCompleteId, setProcessingCompleteId] = useState(null);
    const [processingRejectId, setProcessingRejectId] = useState(null);
    const [actionMessage, setActionMessage] = useState(null);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [creationLogs, setCreationLogs] = useState([]);
    const [approveStreamFailed, setApproveStreamFailed] = useState(false);

    const canAct = isSubApprovalMode
        ? canSubApproveDecommission(userRoles)
        : canExecuteDecommission(userRoles);
    const canComplete = !isSubApprovalMode && canCompleteDecommission(userRoles);

    const fetchPending = useCallback(async () => {
        if (!canAct) {
            setPendingLoading(false);
            setPendingRequests([]);
            return;
        }
        try {
            setPendingLoading(true);
            const response = isSubApprovalMode
                ? await api.multiTenancyDecommission.getPendingSubApprovalRequests()
                : await api.multiTenancyDecommission.getPendingRequests();
            setPendingRequests(Array.isArray(response?.data?.requests) ? response.data.requests : []);
            setPendingError(null);
        } catch (err) {
            setPendingError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load pending decommission requests'
            );
            setPendingRequests([]);
        } finally {
            setPendingLoading(false);
        }
    }, [canAct, isSubApprovalMode]);

    const fetchHistory = useCallback(async () => {
        if (!canAct) {
            setHistoryLoading(false);
            setHistoryRequests([]);
            return;
        }
        try {
            setHistoryLoading(true);
            const response = isSubApprovalMode
                ? await api.multiTenancyDecommission.getSubApprovalHistory()
                : await api.multiTenancyDecommission.getApprovalHistory();
            setHistoryRequests(Array.isArray(response?.data?.requests) ? response.data.requests : []);
            setHistoryError(null);
        } catch (err) {
            setHistoryError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load decommission approval history'
            );
            setHistoryRequests([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [canAct, isSubApprovalMode]);

    const refreshAll = useCallback(async () => {
        await Promise.all([fetchPending(), fetchHistory()]);
    }, [fetchPending, fetchHistory]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    useEffect(() => {
        if (!actionMessage) return undefined;

        const timer = window.setTimeout(() => {
            setActionMessage(null);
        }, DECOMMISSION_MESSAGE_DISMISS_MS);

        return () => window.clearTimeout(timer);
    }, [actionMessage]);

    const fetchApproveCloudDetails = useCallback(async (selectedCloudName) => {
        const trimmedCloudName = (selectedCloudName || '').toString().trim();
        if (!trimmedCloudName) return;
        const cacheKey = trimmedCloudName.toLowerCase();

        if (approveCloudDetailsCache[cacheKey]) {
            setApproveCloudDetails(approveCloudDetailsCache[cacheKey]);
            setApproveCloudDetailsLoading(false);
            setApproveCloudDetailsError(null);
            return;
        }

        setApproveCloudDetailsLoading(true);
        setApproveCloudDetailsError(null);
        setApproveCloudDetails(null);
        try {
            const response = await api.multiTenancyDecommission.getActiveCloudDetails(trimmedCloudName);
            const details = response?.data?.data ?? response?.data ?? null;
            setApproveCloudDetailsCache((prev) => ({
                ...prev,
                [cacheKey]: details
            }));
            if (approveCloudNameRef.current === trimmedCloudName) {
                setApproveCloudDetails(details);
            }
        } catch (err) {
            if (approveCloudNameRef.current === trimmedCloudName) {
                setApproveCloudDetailsError(
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to load cloud details'
                );
            }
        } finally {
            if (approveCloudNameRef.current === trimmedCloudName) {
                setApproveCloudDetailsLoading(false);
            }
        }
    }, [approveCloudDetailsCache]);

    const handleSubApprove = async (request) => {
        const requestId = request.requestId || request.id;
        if (!requestId) return;

        setProcessingApproveId(requestId);
        setActionMessage(null);
        try {
            await api.multiTenancyDecommission.subApprove({
                requestId,
                approvedBy: userEmail
            });
            setPendingRequests((prev) =>
                prev.filter((item) => (item.requestId || item.id) !== requestId)
            );
            setActionMessage({
                type: 'positive',
                text: `Sub-approved decommission request for "${request.cloudName}".`
            });
            refreshAll();
            onActionComplete?.();
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to sub-approve decommission request';
            setActionMessage({
                type: 'negative',
                text: errorMessage
            });
        } finally {
            setProcessingApproveId(null);
        }
    };

    const handleDevopsApprove = async (request) => {
        const requestId = request.requestId || request.id;
        if (!requestId) return;

        setProcessingApproveId(requestId);
        setActionMessage(null);
        setApproveStreamFailed(false);
        setShowLogsModal(true);
        setCreationLogs([]);

        const apiBase = apiClient.defaults.baseURL || 'https://qa-tenantbridge.parablu.com';
        const heartbeatInterval = setInterval(() => {
            setCreationLogs((prev) => [
                ...prev,
                `Still processing decommission... (${new Date().toLocaleTimeString()})`
            ]);
        }, 60000);

        try {
            const response = await fetch(`${apiBase}/api/multi-tenancy-decommission/approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                    Connection: 'keep-alive',
                    'Keep-Alive': 'timeout=600'
                },
                body: JSON.stringify({
                    requestId,
                    approvedBy: userEmail
                }),
                credentials: 'include'
            });

            if (!response.ok) {
                let errMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errBody = await response.json();
                    errMessage = errBody?.message || errMessage;
                } catch {
                    // ignore parse errors
                }
                throw new Error(errMessage);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let streamFailed = false;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        const data = JSON.parse(line);
                        if (data.type === 'log') {
                            setCreationLogs((prev) => [...prev, data.message]);
                        } else if (data.type === 'warning') {
                            const warningLine = (data.message || '').toString();
                            setCreationLogs((prev) => [
                                ...prev,
                                warningLine.startsWith('🔴') ? warningLine : `🔴 ${warningLine}`
                            ]);
                        } else if (data.type === 'error') {
                            streamFailed = true;
                            setApproveStreamFailed(true);
                            const errLine = (data.message || '').toString();
                            setCreationLogs((prev) => [
                                ...prev,
                                errLine.startsWith('❌') ? errLine : `❌ ${errLine}`
                            ]);
                        }
                    } catch {
                        setCreationLogs((prev) => [...prev, line]);
                    }
                }
            }

            setApproveStreamFailed(streamFailed);
            setActionMessage({
                type: streamFailed ? 'negative' : 'positive',
                text: streamFailed
                    ? `Decommission approval for "${request.cloudName}" reported an error. Check logs for details.`
                    : `Approved decommission request for "${request.cloudName}".`
            });
            await refreshAll();
            onActionComplete?.();
        } catch (err) {
            const errorMessage = err?.message || 'Failed to approve decommission request';
            setApproveStreamFailed(true);
            setCreationLogs((prev) => [
                ...prev,
                `❌ Error: ${errorMessage}`
            ]);
            setActionMessage({
                type: 'negative',
                text: errorMessage
            });
        } finally {
            clearInterval(heartbeatInterval);
            setProcessingApproveId(null);
        }
    };

    const handleApprove = (request) => {
        const selectedCloudName = (request?.cloudName || '').toString().trim();
        approveCloudNameRef.current = selectedCloudName;
        setApproveConfirmRequest(request);
        setApproveCloudDetails(null);
        setApproveCloudDetailsError(null);
        setShowApproveConfirmModal(true);
        fetchApproveCloudDetails(selectedCloudName);
    };

    const handleConfirmApprove = async () => {
        const request = approveConfirmRequest;
        if (!request) return;

        setShowApproveConfirmModal(false);
        setApproveConfirmRequest(null);
        setApproveCloudDetails(null);
        setApproveCloudDetailsError(null);
        setApproveCloudDetailsLoading(false);
        approveCloudNameRef.current = '';
        if (isSubApprovalMode) {
            await handleSubApprove(request);
        } else {
            await handleDevopsApprove(request);
        }
    };

    const handleComplete = async (request) => {
        const requestId = request.requestId || request.id;
        if (!requestId) return;

        setProcessingCompleteId(requestId);
        setActionMessage(null);
        try {
            const response = await api.multiTenancyDecommission.complete({
                requestId,
                completedBy: userEmail
            });
            const apiLogs = response?.data?.logs;
            if (Array.isArray(apiLogs) && apiLogs.length) {
                console.group(`[Decommission Complete] ${request.cloudName}`);
                apiLogs.forEach((line) => console.log(line));
                console.groupEnd();
            }
            await refreshAll();
            setActionMessage({
                type: 'positive',
                text: `Manual decommission completion recorded for "${request.cloudName}".`
            });
            onActionComplete?.();
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to complete decommission request';
            setActionMessage({
                type: 'negative',
                text: errorMessage
            });
        } finally {
            setProcessingCompleteId(null);
        }
    };

    const handleRejectSubmit = async (rejectionReason) => {
        const request = selectedRequest;
        const requestId = request?.requestId || request?.id;
        if (!requestId) return;
        setProcessingRejectId(requestId);
        setActionMessage(null);
        try {
            await api.multiTenancyDecommission.reject({
                requestId,
                rejectionReason,
                rejectedBy: userEmail
            });
            setShowRejectionModal(false);
            setSelectedRequest(null);
            setActionMessage({
                type: 'positive',
                text: `Rejected decommission request for "${request.cloudName}".`
            });
            await refreshAll();
            onActionComplete?.();
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to reject decommission request';
            setActionMessage({
                type: 'negative',
                text: errorMessage
            });
        } finally {
            setProcessingRejectId(null);
        }
    };

    if (!canAct) {
        return (
            <Message warning>
                <Message.Header>Approval access required</Message.Header>
                <p>Only users with the {requiredAccessLabel} role can use this view.</p>
            </Message>
        );
    }

    const pendingSectionTitle = isSubApprovalMode
        ? 'Pending Sub-Approvals'
        : 'Pending DevOps Approvals';
    const pendingEmptyMessage = isSubApprovalMode
        ? 'Requests awaiting MT Decommission Sub Approver will appear here.'
        : 'Requests sub-approved and awaiting DevOps execution will appear here.';

    return (
        <>
            {actionMessage && (
                <Message
                    positive={actionMessage.type === 'positive'}
                    negative={actionMessage.type === 'negative'}
                    onDismiss={() => setActionMessage(null)}
                >
                    {actionMessage.text}
                </Message>
            )}

            <Header as="h3" style={sectionHeaderStyle}>
                {pendingSectionTitle}
            </Header>

            {pendingLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <Loader active inline="centered">Loading pending requests...</Loader>
                </div>
            ) : pendingError ? (
                <Message negative>{pendingError}</Message>
            ) : !pendingRequests.length ? (
                <Segment placeholder style={{ minHeight: '160px' }}>
                    <Header icon>
                        <Icon name="check circle outline" color="blue" />
                        No pending requests
                    </Header>
                    <Segment.Inline>
                        <p style={{ color: '#475569', margin: 0 }}>{pendingEmptyMessage}</p>
                    </Segment.Inline>
                </Segment>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <Table celled striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Timestamp</Table.HeaderCell>
                                <Table.HeaderCell>Request ID</Table.HeaderCell>
                                <Table.HeaderCell>Cloud Name</Table.HeaderCell>
                                <Table.HeaderCell>Reason</Table.HeaderCell>
                                <Table.HeaderCell>Submitted By</Table.HeaderCell>
                                {!isSubApprovalMode && (
                                    <Table.HeaderCell>Sub Approved By</Table.HeaderCell>
                                )}
                                <Table.HeaderCell>Current Status</Table.HeaderCell>
                                <Table.HeaderCell>Actions</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {pendingRequests.map((request) => {
                                const requestId = request.requestId || request.id;
                                const isApproving = processingApproveId === requestId;
                                const isCompleting = processingCompleteId === requestId;
                                const isRejecting = processingRejectId === requestId;
                                const awaitingManualCompletion =
                                    request.status === 'DEPROVISIONED_MANUALLY';
                                const isDecommissioning = request.status === 'IN_PROGRESS';
                                const isBusy = isApproving || isCompleting || isRejecting;

                                return (
                                    <Table.Row key={requestId}>
                                        <Table.Cell>{formatTimestamp(request.timestamp)}</Table.Cell>
                                        <Table.Cell>{requestId || '—'}</Table.Cell>
                                        <Table.Cell>{request.cloudName || '—'}</Table.Cell>
                                        <Table.Cell>{displayDecommissionValue(request.decommissionReason)}</Table.Cell>
                                        <Table.Cell>{request.submittedBy || '—'}</Table.Cell>
                                        {!isSubApprovalMode && (
                                            <Table.Cell>{request.subApprovedBy || '—'}</Table.Cell>
                                        )}
                                        <Table.Cell className={getDecommissionActualStatusColor(request.status)}>
                                            {formatDecommissionActualStatus(request.status)}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {awaitingManualCompletion && canComplete ? (
                                                <Button
                                                    primary
                                                    size="small"
                                                    loading={isCompleting}
                                                    disabled={isBusy}
                                                    onClick={() => handleComplete(request)}
                                                >
                                                    Storage Decommission Completed
                                                </Button>
                                            ) : awaitingManualCompletion || isDecommissioning ? null : (
                                                <>
                                                    <Button
                                                        positive
                                                        size="small"
                                                        loading={isApproving}
                                                        disabled={isBusy}
                                                        onClick={() => handleApprove(request)}
                                                    >
                                                        {isSubApprovalMode ? 'Sub Approve' : 'Approve & Execute'}
                                                    </Button>
                                                    <Button
                                                        negative
                                                        size="small"
                                                        style={{ marginLeft: '0.5rem' }}
                                                        loading={isRejecting}
                                                        disabled={isBusy}
                                                        onClick={() => {
                                                            setSelectedRequest(request);
                                                            setShowRejectionModal(true);
                                                        }}
                                                    >
                                                        Reject
                                                    </Button>
                                                </>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </div>
            )}

            <Divider section style={{ marginTop: '2rem', marginBottom: '1.25rem' }} />

            <DecommissionSubmittedRequestsTable
                title={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Icon name="check circle" color="blue" />
                        {isSubApprovalMode ? 'Sub Approval History' : 'Approval History'}
                    </span>
                }
                requests={historyRequests}
                loading={historyLoading}
                error={historyError}
                showSubmittedBy
                showSubApprovedBy
                emptyTitle={
                    isSubApprovalMode ? 'No sub approval history found' : 'No approval history found'
                }
                emptyMessage="Completed decommission requests will appear here."
            />

            <RejectionModal
                open={showRejectionModal}
                onClose={() => {
                    if (!processingRejectId) {
                        setShowRejectionModal(false);
                        setSelectedRequest(null);
                    }
                }}
                onSubmit={handleRejectSubmit}
                isSubmitting={!!processingRejectId}
            />

            <CreationLogsModal
                open={showLogsModal}
                onClose={() => {
                    setShowLogsModal(false);
                    setApproveStreamFailed(false);
                }}
                logs={creationLogs}
                processLabel="Decommissioning"
                operationFailed={approveStreamFailed}
            />

            <Modal
                open={showApproveConfirmModal}
                onClose={() => {
                    setShowApproveConfirmModal(false);
                    setApproveConfirmRequest(null);
                    setApproveCloudDetails(null);
                    setApproveCloudDetailsError(null);
                    setApproveCloudDetailsLoading(false);
                    approveCloudNameRef.current = '';
                }}
                size="small"
            >
                <Modal.Header>
                    {isSubApprovalMode ? 'Confirm Sub Approve' : 'Confirm Approve & Execute'}
                </Modal.Header>
                <Modal.Content>
                    <Message warning>
                        <Message.Header>
                            {isSubApprovalMode ? 'Please confirm before sub approval' : 'Please confirm before execution'}
                        </Message.Header>
                        <p style={{ marginBottom: 0 }}>
                            {isSubApprovalMode
                                ? 'This will sub-approve this request and move it to DevOps approval.'
                                : 'This will approve and start decommission execution for this request.'}
                        </p>
                    </Message>
                    <div style={{ marginTop: '1rem', lineHeight: '1.7' }}>
                        <p style={{ marginBottom: '0.25rem' }}>
                            <strong>Request ID:</strong>{' '}
                            <span style={{ color: '#062D4D' }}>
                                {approveConfirmRequest?.requestId || approveConfirmRequest?.id || 'N/A'}
                            </span>
                        </p>
                        <p style={{ marginBottom: 0 }}>
                            <strong>Cloud Name:</strong>{' '}
                            <span style={{ color: '#062D4D' }}>
                                {approveConfirmRequest?.cloudName || 'N/A'}
                            </span>
                        </p>
                        <p style={{ margin: '0.25rem 0 0' }}>
                            <strong>Reason:</strong>{' '}
                            <span style={{ color: '#062D4D' }}>
                                {approveConfirmRequest?.decommissionReason || 'N/A'}
                            </span>
                        </p>
                    </div>
                    <Divider />
                    <Header as="h4" style={{ color: '#062D4D' }}>
                        Cloud Details
                    </Header>
                    {approveCloudDetailsLoading ? (
                        <Loader active inline="centered">Loading cloud details...</Loader>
                    ) : approveCloudDetailsError ? (
                        <Message negative>
                            <Message.Header>Unable to load cloud details</Message.Header>
                            <p>{approveCloudDetailsError}</p>
                            <Button
                                type="button"
                                size="small"
                                onClick={() => fetchApproveCloudDetails(approveConfirmRequest?.cloudName)}
                            >
                                Retry
                            </Button>
                        </Message>
                    ) : approveCloudDetails ? (
                        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                            {renderActiveCloudDetails(approveCloudDetails)}
                        </div>
                    ) : (
                        <Message info>No cloud details available.</Message>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={() => {
                            setShowApproveConfirmModal(false);
                            setApproveConfirmRequest(null);
                            setApproveCloudDetails(null);
                            setApproveCloudDetailsError(null);
                            setApproveCloudDetailsLoading(false);
                            approveCloudNameRef.current = '';
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        positive
                        onClick={handleConfirmApprove}
                    >
                        <Icon name="check" />
                        {isSubApprovalMode ? 'Confirm Sub Approve' : 'Confirm & Execute'}
                    </Button>
                </Modal.Actions>
            </Modal>

        </>
    );
};

const MultiTenancyDecommission = ({ userEmail, userRoles = [] }) => {
    const showRequestSubTab = canAccessDecommissionRequestSubTab(userRoles);
    const showApprovalSubTab = canAccessDecommissionApprovalSubTab(userRoles);
    const showSubApprovalSubTab = canAccessDecommissionSubApprovalSubTab(userRoles);
    const [activeSubTab, setActiveSubTab] = useState(() => getDefaultDecommissionSubTab(userRoles));
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const allowed =
            (activeSubTab === 'request' && showRequestSubTab) ||
            (activeSubTab === 'approval' && showApprovalSubTab) ||
            (activeSubTab === 'subApproval' && showSubApprovalSubTab);
        if (!allowed) {
            setActiveSubTab(getDefaultDecommissionSubTab(userRoles));
        }
    }, [activeSubTab, showApprovalSubTab, showRequestSubTab, showSubApprovalSubTab, userRoles]);

    const fetchMyRequests = useCallback(async () => {
        if (!userEmail || !showRequestSubTab) return;
        try {
            setLoading(true);
            const response = await api.multiTenancyDecommission.getMyRequests();
            setRequests(Array.isArray(response?.data?.requests) ? response.data.requests : []);
            setError(null);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load submitted requests'
            );
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, [userEmail, showRequestSubTab]);

    useEffect(() => {
        if (activeSubTab === 'request' && showRequestSubTab) {
            fetchMyRequests();
        }
    }, [activeSubTab, fetchMyRequests, showRequestSubTab]);

    const handleApprovalActionComplete = () => {
        fetchMyRequests();
    };

    const subtitle =
        activeSubTab === 'request'
            ? 'Enter the cloud name you want to decommission and submit your request.'
            : activeSubTab === 'subApproval'
                ? 'Sub-approve requests to send them to DevOps for decommission execution.'
                : 'Approve and execute decommission after sub-approval. Use Completed when manual tenant steps apply.';

    return (
        <>
            <div style={{ marginTop: '1rem', marginBottom: '1.25rem' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '1.35rem',
                        fontWeight: 700,
                        color: '#062D4D',
                        marginBottom: '0.5rem'
                    }}
                >
                    <Icon name="power off" color="blue" />
                    <span>Multi Tenancy Decommission</span>
                </div>
                <div style={{ fontWeight: 500, color: '#062D4D' }}>{subtitle}</div>
            </div>

            {(showRequestSubTab || showApprovalSubTab || showSubApprovalSubTab) && (
                <Menu pointing secondary style={{ marginBottom: '1rem' }}>
                    {showRequestSubTab && (
                        <Menu.Item
                            name="Decommission Request"
                            active={activeSubTab === 'request'}
                            onClick={() => setActiveSubTab('request')}
                        />
                    )}
                    {showSubApprovalSubTab && (
                        <Menu.Item
                            name="Decommission Sub Approval"
                            active={activeSubTab === 'subApproval'}
                            onClick={() => setActiveSubTab('subApproval')}
                        />
                    )}
                    {showApprovalSubTab && (
                        <Menu.Item
                            name="Decommission Approval"
                            active={activeSubTab === 'approval'}
                            onClick={() => setActiveSubTab('approval')}
                        />
                    )}
                </Menu>
            )}

            {activeSubTab === 'request' && showRequestSubTab && (
                <DecommissionRequestPanel
                    userEmail={userEmail}
                    requests={requests}
                    loading={loading}
                    error={error}
                    onRefresh={fetchMyRequests}
                />
            )}
            {activeSubTab === 'subApproval' && showSubApprovalSubTab && (
                <DecommissionApproval
                    userEmail={userEmail}
                    userRoles={userRoles}
                    approvalMode="sub"
                    onActionComplete={handleApprovalActionComplete}
                    requiredAccessLabel="MT Decommission Approver"
                />
            )}
            {activeSubTab === 'approval' && showApprovalSubTab && (
                <DecommissionApproval
                    userEmail={userEmail}
                    userRoles={userRoles}
                    approvalMode="devops"
                    onActionComplete={handleApprovalActionComplete}
                    requiredAccessLabel="DevOps"
                />
            )}
        </>
    );
};

export default MultiTenancyDecommission;
